import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  Minus,
  Eye,
  EyeOff,
  ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Curated Google Fonts list — must match the wrapped-page editor exactly.
const GOOGLE_FONTS = [
  "Anton",
  "Archivo Black",
  "Bebas Neue",
  "Bricolage Grotesque",
  "Cormorant Garamond",
  "DM Serif Display",
  "Fraunces",
  "Inter",
  "Instrument Serif",
  "JetBrains Mono",
  "Libre Bodoni",
  "Libre Caslon Display",
  "Lora",
  "Manrope",
  "Merriweather",
  "Montserrat",
  "Oswald",
  "Outfit",
  "Playfair Display",
  "Plus Jakarta Sans",
  "Poppins",
  "Raleway",
  "Roboto",
  "Roboto Slab",
  "Source Serif 4",
  "Space Grotesk",
  "Syne",
  "Unbounded",
  "Urbanist",
  "Work Sans",
];

function loadGoogleFontOnce(family) {
  if (!family) return;
  const id = `gfedit-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family
  )}:wght@400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

const DEFAULT_FORM = {
  bg_color: "#0A0A0A",
  popup_height: "3/4",
  title: "",
  theme_color: "",
  header_mode: "name",
  header_align: "center",
  header_size: 64,
  header_color: "#FFFFFF",
  header_font: "",
  logo_url: "",
  logo_offset: 0,
  logo_offset_x: 0,
  heading: "Welcome",
  heading_color: "#0A0A0A",
  heading_size: 32,
  heading_font: "",
  button_bg: "#0A0A0A",
  button_text_color: "#FFFFFF",
  button_font: "",
  button_text_size: 14,
  go_to_website_text: "Go to Website",
  go_to_website_url: "",
  buttons: Array.from({ length: 10 }, () => ({ enabled: false, text: "", url: "", popup_size: "4/5" })),
};

export default function LandingEditor() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/landing")
      .then((res) => {
        if (!alive) return;
        const d = res.data || {};
        setForm({
          ...DEFAULT_FORM,
          ...d,
          theme_color: d.theme_color || "",
          header_font: d.header_font || "",
          heading_font: d.heading_font || "",
          button_font: d.button_font || "",
          logo_url: d.logo_url || "",
          buttons:
            Array.isArray(d.buttons) && d.buttons.length === 10
              ? d.buttons.map((b) => ({
                  enabled: !!b.enabled,
                  text: b.text || "",
                  url: b.url || "",
                  popup_size: b.popup_size || "4/5",
                }))
              : DEFAULT_FORM.buttons,
        });
      })
      .catch(() => toast.error("Could not load landing page settings"))
      .finally(() => setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setButton = (idx, patch) =>
    setForm((f) => ({
      ...f,
      buttons: f.buttons.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));

  const onUploadLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error("Logo must be under 600KB. Compress or use a hosted URL.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title?.trim() || null,
        theme_color: form.theme_color?.trim() || null,
        header_font: form.header_font?.trim() || null,
        heading_font: form.heading_font?.trim() || null,
        button_font: form.button_font?.trim() || null,
        logo_url: form.logo_url?.trim() || null,
        header_size: Number(form.header_size) || 64,
        heading_size: Number(form.heading_size) || 32,
        button_text_size: Number(form.button_text_size) || 14,
        logo_offset: Number(form.logo_offset) || 0,
        logo_offset_x: Number(form.logo_offset_x) || 0,
      };
      await api.put("/landing", payload);
      toast.success("Landing page saved");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-black"
            data-testid="landing-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <h1 className="text-sm font-bold uppercase tracking-[0.2em]">Landing Page</h1>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-10 rounded-md bg-black text-white hover:bg-zinc-800"
            data-testid="landing-save"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {/* Section: Look & feel (same tools as wrapped pages) */}
        <section className="space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Look & feel
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Background color (hex)">
              <Input
                type="text"
                value={form.bg_color}
                onChange={(e) => setField("bg_color", e.target.value)}
                placeholder="#0A0A0A"
                data-testid="landing-bg-color"
                className="font-mono"
              />
            </Field>
            <Field label="Mobile status / footer color (hex, optional)">
              <Input
                type="text"
                value={form.theme_color}
                onChange={(e) => setField("theme_color", e.target.value)}
                placeholder="leave blank for native"
                data-testid="landing-theme-color"
                className="font-mono"
              />
            </Field>
          </div>

          <Field label="Popup height">
            <div className="flex gap-2 flex-wrap">
              {["1/4", "2/4", "3/4", "4/5"].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setField("popup_height", h)}
                  className={`h-10 px-4 rounded-md border text-sm font-mono ${
                    form.popup_height === h
                      ? "bg-black text-white border-black"
                      : "border-zinc-300 text-zinc-700"
                  }`}
                  data-testid={`landing-height-${h.replace("/", "-")}`}
                >
                  {h}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Header mode">
              <div className="flex gap-2">
                {["name", "logo", "none"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setField("header_mode", m)}
                    className={`h-10 px-4 rounded-md border text-sm capitalize ${
                      form.header_mode === m
                        ? "bg-black text-white border-black"
                        : "border-zinc-300 text-zinc-700"
                    }`}
                    data-testid={`landing-header-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Header alignment">
              <div className="flex gap-2">
                {["left", "center", "right"].map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setField("header_align", a)}
                    className={`h-10 px-4 rounded-md border text-sm capitalize ${
                      form.header_align === a
                        ? "bg-black text-white border-black"
                        : "border-zinc-300 text-zinc-700"
                    }`}
                    data-testid={`landing-header-align-${a}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title text (used when header mode = name)">
              <Input
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="GS Litigation"
                data-testid="landing-title"
              />
            </Field>
            <Field label="Header color (hex)">
              <Input
                value={form.header_color}
                onChange={(e) => setField("header_color", e.target.value)}
                className="font-mono"
                data-testid="landing-header-color"
              />
            </Field>
            <Field label="Header font (Google Font, optional)">
              <FontSelect
                value={form.header_font}
                onChange={(v) => setField("header_font", v)}
                testid="landing-header-font"
              />
            </Field>
            <NumberField
              label="Header / logo size (px)"
              value={form.header_size}
              setValue={(v) => setField("header_size", v)}
              min={12}
              max={600}
              step={8}
              testid="landing-header-size"
            />
          </div>

          {form.header_mode === "logo" && (
            <div className="space-y-6 border-t border-zinc-200 pt-6">
              {/* Logo upload — identical UX to wrapped pages */}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Logo
                </Label>
                <div className="flex items-center gap-3">
                  <div
                    className="h-16 w-16 rounded-md border border-zinc-300 bg-zinc-50 overflow-hidden flex items-center justify-center"
                    data-testid="landing-logo-preview"
                  >
                    {form.logo_url ? (
                      <img
                        src={form.logo_url}
                        alt="Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <ImagePlus className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 rounded-md border-zinc-300"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="landing-logo-upload-btn"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" /> Upload
                  </Button>
                  {form.logo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-11 text-red-600"
                      onClick={() => setField("logo_url", "")}
                      data-testid="landing-logo-clear-btn"
                    >
                      Remove
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onUploadLogo}
                    data-testid="landing-logo-file-input"
                  />
                </div>
                <Input
                  className="h-11 rounded-md border-zinc-300 text-xs"
                  placeholder="…or paste an https:// image URL"
                  value={
                    form.logo_url && !form.logo_url.startsWith("data:")
                      ? form.logo_url
                      : ""
                  }
                  onChange={(e) => setField("logo_url", e.target.value)}
                  data-testid="landing-logo-url-input"
                />
              </div>

              {/* Vertical slide — directional Up/Down arrows */}
              <ArrowOffsetField
                label="Slide behind popup"
                helper="Push the logo down so part of it tucks behind the popup. Positive = slide down."
                value={form.logo_offset}
                setValue={(v) => setField("logo_offset", v)}
                min={-300}
                max={1000}
                step={16}
                iconDown={ArrowDown}
                iconUp={ArrowUp}
                testid="landing-logo-offset"
              />

              {/* Horizontal slide — directional Left/Right arrows */}
              <ArrowOffsetField
                label="Slide left / right"
                helper="Nudge the logo horizontally. Positive = right."
                value={form.logo_offset_x}
                setValue={(v) => setField("logo_offset_x", v)}
                min={-400}
                max={400}
                step={16}
                iconDown={ArrowRight}
                iconUp={ArrowLeft}
                horizontal
                testid="landing-logo-offset-x"
              />
            </div>
          )}
        </section>

        {/* Section: Popup heading */}
        <section className="space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Popup heading
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Heading text">
              <Input
                value={form.heading}
                onChange={(e) => setField("heading", e.target.value)}
                placeholder="Welcome"
                data-testid="landing-heading-text"
              />
            </Field>
            <Field label="Heading color (hex)">
              <Input
                value={form.heading_color}
                onChange={(e) => setField("heading_color", e.target.value)}
                className="font-mono"
                data-testid="landing-heading-color"
              />
            </Field>
            <NumberField
              label="Heading size (px)"
              value={form.heading_size}
              setValue={(v) => setField("heading_size", v)}
              min={12}
              max={200}
              step={2}
              testid="landing-heading-size"
            />
            <Field label="Heading font (Google Font, optional)">
              <FontSelect
                value={form.heading_font}
                onChange={(v) => setField("heading_font", v)}
                testid="landing-heading-font"
              />
            </Field>
          </div>
        </section>

        {/* Section: Buttons */}
        <section className="space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Buttons (up to 10 slots)
          </h2>
          <p className="text-xs text-zinc-500">
            How many buttons fit per page is set by the popup height:
            1/4 hides them entirely, 2/4 = 2, 3/4 = 6, 4/5 = 8. Any extras spill
            into additional pages that visitors swipe (or arrow) through.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Button background color (hex)">
              <Input
                value={form.button_bg}
                onChange={(e) => setField("button_bg", e.target.value)}
                className="font-mono"
                data-testid="landing-btn-bg"
              />
            </Field>
            <Field label="Button text color (hex)">
              <Input
                value={form.button_text_color}
                onChange={(e) => setField("button_text_color", e.target.value)}
                className="font-mono"
                data-testid="landing-btn-text-color"
              />
            </Field>
            <Field label="Button font (Google Font, optional)">
              <FontSelect
                value={form.button_font}
                onChange={(v) => setField("button_font", v)}
                testid="landing-btn-font"
              />
            </Field>
            <NumberField
              label="Button text size (px)"
              value={form.button_text_size}
              setValue={(v) => setField("button_text_size", v)}
              min={8}
              max={48}
              step={1}
              testid="landing-btn-text-size"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            {form.buttons.map((b, i) => (
              <div
                key={i}
                className="rounded-lg border border-zinc-200 p-3 space-y-2"
                style={{
                  gridColumn: (i % 2) + 1,
                  gridRow: Math.floor(i / 2) + 1,
                }}
              >
                <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  <span>Slot {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => setButton(i, { enabled: !b.enabled })}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
                      b.enabled
                        ? "bg-emerald-500 text-white"
                        : "bg-zinc-200 text-zinc-700"
                    }`}
                    data-testid={`landing-btn-toggle-${i}`}
                  >
                    {b.enabled ? (
                      <>
                        <Eye className="w-3.5 h-3.5" /> Visible
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-3.5 h-3.5" /> Hidden
                      </>
                    )}
                  </button>
                </div>
                <Input
                  value={b.text}
                  onChange={(e) => setButton(i, { text: e.target.value })}
                  placeholder="Button label"
                  data-testid={`landing-btn-text-${i}`}
                />
                <Input
                  value={b.url}
                  onChange={(e) => setButton(i, { url: e.target.value })}
                  placeholder="https://…"
                  data-testid={`landing-btn-url-${i}`}
                />
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Popup size
                  </Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {["3/4", "4/5", "5/6", "full"].map((sz) => (
                      <button
                        key={sz}
                        type="button"
                        onClick={() => setButton(i, { popup_size: sz })}
                        className={`h-8 px-2.5 rounded-md border text-xs font-mono ${
                          (b.popup_size || "4/5") === sz
                            ? "bg-black text-white border-black"
                            : "border-zinc-300 text-zinc-700"
                        }`}
                        data-testid={`landing-btn-popup-size-${i}-${sz.replace("/", "-")}`}
                      >
                        {sz}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section: Go to Website */}
        <section className="space-y-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Go to Website (always visible, bottom of popup)
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Button text">
              <Input
                value={form.go_to_website_text}
                onChange={(e) => setField("go_to_website_text", e.target.value)}
                data-testid="landing-go-text"
              />
            </Field>
            <Field label="URL (https://…)">
              <Input
                value={form.go_to_website_url}
                onChange={(e) => setField("go_to_website_url", e.target.value)}
                placeholder="https://www.gslitigation.com"
                data-testid="landing-go-url"
              />
            </Field>
          </div>
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

function NumberField({ label, value, setValue, min, max, step, testid }) {
  const clamp = (n) => Math.max(min, Math.min(max, Math.round(n)));
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</Label>
      <div className="inline-flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-10 w-10 rounded-md border-zinc-300"
          onClick={() => setValue(clamp(Number(value) - step))}
          data-testid={`${testid}-down`}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <div className="h-10 min-w-[88px] px-2 rounded-md border border-zinc-300 inline-flex items-center justify-center">
          <input
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => {
              const n = Number(e.target.value);
              setValue(Number.isFinite(n) ? clamp(n) : min);
            }}
            style={{ fontSize: "16px" }}
            className="w-14 text-center bg-transparent outline-none font-mono tabular-nums"
            data-testid={`${testid}-value`}
          />
          <span className="text-sm text-zinc-500 font-mono">px</span>
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-10 rounded-md border-zinc-300"
          onClick={() => setValue(clamp(Number(value) + step))}
          data-testid={`${testid}-up`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function FontSelect({ value, onChange, testid }) {
  useEffect(() => {
    if (value) loadGoogleFontOnce(value);
  }, [value]);
  return (
    <Select
      value={value || "__default__"}
      onValueChange={(v) => onChange(v === "__default__" ? "" : v)}
    >
      <SelectTrigger className="h-11 rounded-md border-zinc-300" data-testid={testid}>
        <SelectValue placeholder="Default" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        <SelectItem value="__default__">Default</SelectItem>
        {GOOGLE_FONTS.map((f) => (
          <SelectItem
            key={f}
            value={f}
            style={{ fontFamily: `'${f}', sans-serif` }}
            onMouseEnter={() => loadGoogleFontOnce(f)}
          >
            {f}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Arrow-style offset stepper. Used for "Slide up/down" (vertical) and
// "Slide left/right" (horizontal) — same UX as wrapped-page editor.
function ArrowOffsetField({
  label,
  helper,
  value,
  setValue,
  min,
  max,
  step,
  iconDown,
  iconUp,
  horizontal = false,
  testid,
}) {
  const clamp = (n) => Math.max(min, Math.min(max, Math.round(n)));
  const IconA = iconUp;
  const IconB = iconDown;
  const aSuffix = horizontal ? "left" : "up";
  const bSuffix = horizontal ? "right" : "down";
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </Label>
      {helper && <p className="text-xs text-zinc-500">{helper}</p>}
      <div className="inline-flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-11 w-11 rounded-md border-zinc-300"
          onClick={() => setValue(clamp(Number(value) - step))}
          data-testid={`${testid}-${aSuffix}`}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          <IconA className="w-4 h-4" />
        </Button>
        <div className="h-11 min-w-[88px] px-2 rounded-md border border-zinc-300 inline-flex items-center justify-center">
          <input
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => {
              const n = Number(e.target.value);
              setValue(Number.isFinite(n) ? clamp(n) : 0);
            }}
            style={{ fontSize: "16px" }}
            className="w-14 text-center bg-transparent outline-none font-mono tabular-nums"
            data-testid={`${testid}-value`}
          />
          <span className="text-sm text-zinc-500 font-mono">px</span>
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-11 rounded-md border-zinc-300"
          onClick={() => setValue(clamp(Number(value) + step))}
          data-testid={`${testid}-${bSuffix}`}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          <IconB className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
